import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import type { AdminTheme } from '@/context/AdminThemeContext';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';

interface AdminDetailSectionProps {
  title: string;
  icon: LucideIcon;
  iconClassName?: string;
  theme: AdminTheme;
  children: ReactNode;
  className?: string;
}

export function AdminDetailSection({
  title,
  icon: Icon,
  iconClassName = 'text-primary',
  theme,
  children,
  className,
}: AdminDetailSectionProps) {
  const shell = adminShell(theme);

  return (
    <Card className={cn('overflow-hidden', shell.card, className)}>
      <div className={cn('flex items-center gap-2 border-b px-4 py-3 sm:px-5', shell.tableWrap)}>
        <Icon className={cn('h-4 w-4 shrink-0', iconClassName)} />
        <h2 className="text-sm font-bold">{title}</h2>
      </div>
      <div className="space-y-4 p-4 sm:p-5">{children}</div>
    </Card>
  );
}

interface AdminDetailRowProps {
  label: string;
  value: ReactNode;
  theme: AdminTheme;
  mono?: boolean;
  highlight?: boolean;
}

export function AdminDetailRow({ label, value, theme, mono = false, highlight = false }: AdminDetailRowProps) {
  const shell = adminShell(theme);

  return (
    <div className={cn('flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4')}>
      <span className={cn('shrink-0 text-xs font-medium', shell.cardMuted)}>{label}</span>
      <span
        className={cn(
          'break-all text-sm sm:max-w-[68%] sm:text-right',
          mono && 'font-mono text-xs',
          highlight ? 'text-lg font-bold text-emerald-500' : shell.tableCell,
          !highlight && 'font-medium',
        )}
      >
        {value}
      </span>
    </div>
  );
}

interface AdminInfoGridProps {
  theme: AdminTheme;
  children: ReactNode;
  columns?: 1 | 2;
}

export function AdminInfoGrid({ theme, children, columns = 2 }: AdminInfoGridProps) {
  return (
    <div
      className={cn(
        'grid gap-4',
        columns === 2 && 'sm:grid-cols-2',
        '[&>div]:rounded-xl [&>div]:border [&>div]:p-3',
        theme === 'dark' ? '[&>div]:border-slate-800 [&>div]:bg-slate-800/40' : '[&>div]:border-gray-100 [&>div]:bg-brand-surface/60',
      )}
    >
      {children}
    </div>
  );
}

interface AdminInfoFieldProps {
  label: string;
  value: ReactNode;
  theme: AdminTheme;
}

export function AdminInfoField({ label, value, theme }: AdminInfoFieldProps) {
  const shell = adminShell(theme);

  return (
    <div>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', shell.cardMuted)}>{label}</p>
      <div className={cn('mt-1.5 text-sm font-semibold', shell.tableCell)}>{value}</div>
    </div>
  );
}
